// ForkModal — Radix Dialog that branches a new campaign from a given
// iteration of the current one. Budget and batchSize can be optionally
// overridden.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Callout,
  Dialog,
  Flex,
  Slider,
  Text,
  TextField,
} from '@radix-ui/themes';

import { useForkMutation } from '../api';

interface Props {
  campaignId: string;
  currentIteration: number;
  open: boolean;
  onClose: () => void;
}

interface ForkInput {
  atIteration: number;
  overrides?: { budget?: number; batchSize?: number };
}

interface ForkResult {
  id: string;
}

export function ForkModal({ campaignId, currentIteration, open, onClose }: Props) {
  const navigate = useNavigate();
  // W2's mutation signature: mutateAsync({atIteration, overrides}) → { id }.
  // Typed loosely here so we don't create a circular import with W2.
  const fork = useForkMutation(campaignId) as unknown as {
    mutateAsync: (input: ForkInput) => Promise<ForkResult>;
    isPending?: boolean;
  };

  const [atIteration, setAtIteration] = useState(currentIteration);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [budget, setBudget] = useState<string>('');
  const [batchSize, setBatchSize] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state each time the dialog opens.
  useEffect(() => {
    if (open) {
      setAtIteration(currentIteration);
      setOverridesOpen(false);
      setBudget('');
      setBatchSize('');
      setSubmitError(null);
    }
  }, [open, currentIteration]);

  const parsedBudget = budget.trim() === '' ? undefined : Number(budget);
  const parsedBatchSize = batchSize.trim() === '' ? undefined : Number(batchSize);
  const budgetInvalid =
    parsedBudget !== undefined && (!Number.isFinite(parsedBudget) || parsedBudget <= 0);
  const batchSizeInvalid =
    parsedBatchSize !== undefined &&
    (!Number.isFinite(parsedBatchSize) || parsedBatchSize <= 0);

  const canSubmit =
    atIteration >= 0 &&
    atIteration <= currentIteration &&
    !budgetInvalid &&
    !batchSizeInvalid &&
    !fork.isPending;

  const handleFork = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    const overrides: NonNullable<ForkInput['overrides']> = {};
    if (parsedBudget !== undefined) overrides.budget = parsedBudget;
    if (parsedBatchSize !== undefined) overrides.batchSize = parsedBatchSize;
    const input: ForkInput = {
      atIteration,
      overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    };
    try {
      const result = await fork.mutateAsync(input);
      onClose();
      navigate(`/campaigns/${result.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Fork failed.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <Dialog.Content style={{ maxWidth: 480 }}>
        <Dialog.Title>Fork campaign</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Branch a new campaign from this one at a chosen iteration. Existing
          observations up to that iteration are carried forward.
        </Dialog.Description>

        <Flex direction="column" gap="4" style={{ marginTop: 16 }}>
          <div>
            <Flex justify="between" align="center" style={{ marginBottom: 6 }}>
              <Text as="label" size="2" weight="medium">
                Fork at iteration
              </Text>
              <Text size="2" color="gray">
                {atIteration} / {currentIteration}
              </Text>
            </Flex>
            <Slider
              value={[atIteration]}
              min={0}
              max={Math.max(0, currentIteration)}
              step={1}
              onValueChange={(v) => setAtIteration(v[0] ?? 0)}
            />
          </div>

          <div>
            <button
              onClick={() => setOverridesOpen((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent-11)',
                cursor: 'pointer',
                padding: 0,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {overridesOpen ? '▾' : '▸'} Config overrides (optional)
            </button>

            {overridesOpen && (
              <Flex direction="column" gap="2" style={{ marginTop: 8, paddingLeft: 12 }}>
                <Flex gap="3" wrap="wrap">
                  <div style={{ minWidth: 140 }}>
                    <Text as="label" size="2">
                      Budget
                    </Text>
                    <TextField.Root
                      type="number"
                      placeholder="inherit"
                      value={budget}
                      onChange={(e) => setBudget(e.currentTarget.value)}
                      color={budgetInvalid ? 'red' : undefined}
                    />
                    {budgetInvalid && (
                      <Text size="1" color="red">
                        Must be a positive number.
                      </Text>
                    )}
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <Text as="label" size="2">
                      Batch size
                    </Text>
                    <TextField.Root
                      type="number"
                      placeholder="inherit"
                      value={batchSize}
                      onChange={(e) => setBatchSize(e.currentTarget.value)}
                      color={batchSizeInvalid ? 'red' : undefined}
                    />
                    {batchSizeInvalid && (
                      <Text size="1" color="red">
                        Must be a positive number.
                      </Text>
                    )}
                  </div>
                </Flex>
              </Flex>
            )}
          </div>

          {submitError && (
            <Callout.Root color="red">
              <Callout.Text>{submitError}</Callout.Text>
            </Callout.Root>
          )}
        </Flex>

        <Flex justify="end" gap="2" style={{ marginTop: 20 }}>
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={onClose}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={handleFork}
            disabled={!canSubmit}
            loading={fork.isPending}
          >
            Fork
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default ForkModal;
